/**
 * AdminIonUsageReports — LLM-generated aggregate usage reports (GDPR
 * replacement for the removed per-topic question drill-down).
 *
 * Map-reduce on the backend: Haiku digests each conversation once (cached in
 * ion_conversation_digests), one Sonnet call turns all digests in the window
 * into a Markdown report. No admin ever reads raw conversation text.
 *
 * Conventions mirrored from siblings:
 *  - authenticatedFetch + getApiBase (AdminIonAnalyticsSubtab)
 *  - self-rescheduling setTimeout poll while a run is active (AdminIonEvalSubtab)
 *  - blob download (AdminIonEvalSubtab.downloadJsonBundle, adapted to .md)
 *  - native window.confirm for the pre-spend confirmation (admin-wide pattern)
 *  - renderSafeHtml from the chat MarkdownRenderer for the report view
 */
import { useEffect, useRef, useState } from 'preact/hooks';
import { authenticatedFetch } from '../../services/authService.js';
import { getApiBase } from './analytics/shared/formatters.js';
import { renderSafeHtml } from '../chat/MarkdownRenderer.jsx';

const SEGMENT_ROLES = ['batemo', 'customer'];
const POLL_MS = 3000;
const ESTIMATE_DEBOUNCE_MS = 600;

function isoDaysAgo(days) {
  const d = new Date();
  d.setDate(d.getDate() - days);
  return d.toISOString().slice(0, 10);
}

function fmtUsd(v) {
  return v == null ? '—' : `$${Number(v).toFixed(2)}`;
}

async function api(path, opts = {}) {
  const res = await authenticatedFetch(`${getApiBase()}/admin/ion-analytics/reports${path}`, {
    ...opts,
    headers: { 'Content-Type': 'application/json', ...(opts.headers || {}) },
  });
  if (res.status === 204) return null;
  const data = await res.json().catch(() => null);
  if (!res.ok) {
    const detail = typeof data?.detail === 'string' ? data.detail : `HTTP ${res.status}`;
    throw new Error(detail);
  }
  return data;
}

function estimateText(estimate) {
  if (!estimate) return '—';
  const heur = estimate.method === 'chars_heuristic' ? ' (heuristic estimate)' : '';
  return (
    `~${fmtUsd(estimate.total_usd)} total — map ${fmtUsd(estimate.map?.est_usd)} ` +
    `(${estimate.new_digests} new digests, ${estimate.cached_digests} cached) + ` +
    `report ${fmtUsd(estimate.reduce?.est_usd)} — ${estimate.conversations} conversations${heur}`
  );
}

export default function AdminIonUsageReports() {
  const [mode, setMode] = useState('full');
  const [dateFrom, setDateFrom] = useState(isoDaysAgo(30));
  const [dateTo, setDateTo] = useState(isoDaysAgo(0));
  const [roles, setRoles] = useState([...SEGMENT_ROLES]);
  const [estimate, setEstimate] = useState(null);
  const [estimating, setEstimating] = useState(false);
  const [reports, setReports] = useState([]);
  const [openReport, setOpenReport] = useState(null); // full detail row
  const [error, setError] = useState(null);
  const [busy, setBusy] = useState(false);
  const timerRef = useRef(null);

  const body = () => ({
    mode,
    since: new Date(`${dateFrom}T00:00:00Z`).toISOString(),
    until: new Date(`${dateTo}T23:59:59Z`).toISOString(),
    roles,
  });

  // Debounced live estimate whenever the config changes
  useEffect(() => {
    if (!roles.length) {
      setEstimate(null);
      return undefined;
    }
    setEstimating(true);
    const t = setTimeout(() => {
      api('/estimate', { method: 'POST', body: JSON.stringify(body()) })
        .then((e) => {
          setEstimate(e);
          setError(null);
        })
        .catch((e) => setError(e.message))
        .finally(() => setEstimating(false));
    }, ESTIMATE_DEBOUNCE_MS);
    return () => clearTimeout(t);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [mode, dateFrom, dateTo, roles.join(',')]);

  // Report list + self-rescheduling poll while anything is queued/running
  const loadReports = () => {
    api('')
      .then((data) => {
        const rows = data?.reports || [];
        setReports(rows);
        const active = rows.some((r) => r.status === 'queued' || r.status === 'running');
        if (timerRef.current) clearTimeout(timerRef.current);
        if (active) timerRef.current = setTimeout(loadReports, POLL_MS);
      })
      .catch((e) => setError(e.message));
  };
  useEffect(() => {
    loadReports();
    return () => {
      if (timerRef.current) clearTimeout(timerRef.current);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const anyActive = reports.some((r) => r.status === 'queued' || r.status === 'running');

  const handleGenerate = async () => {
    const label = mode === 'full' ? 'Full analysis' : 'User questions only';
    // Decision 2 (2026-07-22): every generation confirms the cost estimate.
    if (!window.confirm(`Generate "${label}" usage report?\n\n${estimateText(estimate)}`)) return;
    setBusy(true);
    try {
      await api('', { method: 'POST', body: JSON.stringify(body()) });
      setError(null);
      loadReports();
    } catch (e) {
      setError(e.message);
    } finally {
      setBusy(false);
    }
  };

  const handleOpen = (id) => {
    api(`/${id}`).then(setOpenReport).catch((e) => setError(e.message));
  };

  const handleDownload = (detail) => {
    const blob = new Blob([detail.report_md || ''], { type: 'text/markdown' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `ion-usage-report-${detail.id}-${detail.mode}.md`;
    document.body.appendChild(a);
    a.click();
    a.remove();
    URL.revokeObjectURL(url);
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Delete this report permanently?')) return;
    try {
      await api(`/${id}`, { method: 'DELETE' });
      if (openReport?.id === id) setOpenReport(null);
      loadReports();
    } catch (e) {
      setError(e.message);
    }
  };

  const toggleRole = (role) =>
    setRoles((prev) => (prev.includes(role) ? prev.filter((r) => r !== role) : [...prev, role]));

  return (
    <section style={{ marginTop: 32 }}>
      <h3 className="section-title">Usage Reports</h3>
      <p className="admin-aim-field-hint" style={{ marginTop: -4, marginBottom: 12 }}>
        LLM-generated aggregate reports on how users use Ion — no admin ever reads raw
        conversation text. Per-conversation digests are cached, so regenerating over the same
        window is cheap.
      </p>
      {error && <div className="admin-aim-error-banner">{error}</div>}

      {/* Config row */}
      <div
        style={{
          display: 'flex',
          flexWrap: 'wrap',
          gap: 12,
          alignItems: 'flex-end',
          marginBottom: 8,
        }}
      >
        <label style={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
          <span className="admin-aim-field-hint" style={{ margin: 0 }}>Mode</span>
          <select
            className="analytics-period-select"
            value={mode}
            onChange={(e) => setMode(e.target.value)}
          >
            <option value="full">Full analysis</option>
            <option value="questions_only">User questions only — cheaper</option>
          </select>
        </label>
        <label style={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
          <span className="admin-aim-field-hint" style={{ margin: 0 }}>From</span>
          <input
            type="date"
            className="admin-input"
            value={dateFrom}
            onInput={(e) => setDateFrom(e.target.value)}
          />
        </label>
        <label style={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
          <span className="admin-aim-field-hint" style={{ margin: 0 }}>To</span>
          <input
            type="date"
            className="admin-input"
            value={dateTo}
            onInput={(e) => setDateTo(e.target.value)}
          />
        </label>
        <div style={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
          <span className="admin-aim-field-hint" style={{ margin: 0 }}>Roles</span>
          <div style={{ display: 'flex', gap: 6 }}>
            {SEGMENT_ROLES.map((role) => (
              <button
                key={role}
                type="button"
                className={`admin-badge admin-badge--clickable ${
                  roles.includes(role) ? 'admin-badge--accent' : 'admin-badge--inactive'
                }`}
                onClick={() => toggleRole(role)}
              >
                {role}
              </button>
            ))}
          </div>
        </div>
        <button
          type="button"
          className="admin-btn admin-btn--primary"
          disabled={busy || anyActive || !roles.length}
          onClick={handleGenerate}
          title={anyActive ? 'A report is already running' : ''}
        >
          {anyActive ? 'Report running…' : 'Generate report'}
        </button>
      </div>

      {/* Live estimate readout */}
      <p className="admin-aim-field-hint" style={{ marginTop: 0, marginBottom: 12 }}>
        {estimating ? 'Estimating…' : estimateText(estimate)}
      </p>

      {/* Report list */}
      {reports.length === 0 ? (
        <div className="empty-state">No reports yet.</div>
      ) : (
        <table className="analytics-table" style={{ width: '100%', fontSize: 13 }}>
          <thead>
            <tr>
              <th style={{ textAlign: 'left' }}>Window</th>
              <th>Mode</th>
              <th>Roles</th>
              <th style={{ textAlign: 'left' }}>Status</th>
              <th>Convs</th>
              <th>Cost</th>
              <th>Created</th>
              <th />
            </tr>
          </thead>
          <tbody>
            {reports.map((r) => (
              <tr key={r.id}>
                <td style={{ textAlign: 'left' }}>
                  {r.since?.slice(0, 10)} → {r.until?.slice(0, 10)}
                </td>
                <td>{r.mode === 'full' ? 'Full' : 'Questions'}</td>
                <td>{(r.roles || []).join(', ')}</td>
                <td style={{ textAlign: 'left' }}>
                  {r.status}
                  {r.status === 'failed' && r.error ? ` — ${r.error.slice(0, 60)}` : ''}
                  {r.stale_conversations > 0 && (
                    <span
                      className="admin-badge admin-badge--warning"
                      style={{ marginLeft: 6 }}
                      title={`${r.stale_conversations} conversations in this window changed since generation — regenerating re-digests only those`}
                    >
                      stale: {r.stale_conversations}
                    </span>
                  )}
                </td>
                <td style={{ textAlign: 'right' }}>{r.conversation_count ?? '—'}</td>
                <td style={{ textAlign: 'right' }}>{fmtUsd(r.cost_usd)}</td>
                <td>{r.created_at?.slice(0, 16).replace('T', ' ')}</td>
                <td style={{ whiteSpace: 'nowrap' }}>
                  {r.status === 'succeeded' && (
                    <>
                      <button
                        type="button"
                        className="admin-ai-btn admin-ai-btn--ghost"
                        onClick={() => handleOpen(r.id)}
                      >
                        Open
                      </button>{' '}
                      <button
                        type="button"
                        className="admin-ai-btn admin-ai-btn--ghost"
                        title="Download as Markdown"
                        onClick={() =>
                          api(`/${r.id}`).then(handleDownload).catch((e) => setError(e.message))
                        }
                      >
                        .md
                      </button>
                    </>
                  )}{' '}
                  <button
                    type="button"
                    className="admin-ai-btn admin-ai-btn--ghost"
                    title="Delete report"
                    onClick={() => handleDelete(r.id)}
                  >
                    ✕
                  </button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      )}

      {/* Report view modal */}
      {openReport && (
        <div className="admin-modal-overlay" onClick={() => setOpenReport(null)}>
          <div
            className="admin-modal admin-modal--glass"
            style={{ maxWidth: 900, width: '90%' }}
            onClick={(e) => e.stopPropagation()}
          >
            <div className="admin-modal-header">
              <h3>
                Usage report #{openReport.id} — {openReport.since?.slice(0, 10)} →{' '}
                {openReport.until?.slice(0, 10)}
              </h3>
              <button
                type="button"
                className="admin-modal-close"
                onClick={() => setOpenReport(null)}
              >
                ×
              </button>
            </div>
            <div className="admin-modal-body" style={{ maxHeight: '70vh', overflowY: 'auto' }}>
              <div
                className="ion-md"
                // renderSafeHtml sanitizes via DOMPurify (same path as chat)
                dangerouslySetInnerHTML={{ __html: renderSafeHtml(openReport.report_md || '') }}
              />
            </div>
            <div className="admin-modal-footer">
              <button
                type="button"
                className="admin-btn admin-btn--secondary"
                onClick={() => handleDownload(openReport)}
              >
                Download .md
              </button>
            </div>
          </div>
        </div>
      )}
    </section>
  );
}
